import { useEffect, useState } from 'react'
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react'
import { cn } from './internal/utils'
import { BORDER, RADIUS_MD } from './internal/presets'
import { mountDetached } from './internal/reactRootHost'

const ICONS = {
  success: { Icon: CheckCircle2, color: 'var(--success-green)' },
  error: { Icon: XCircle, color: 'var(--error-color)' },
  warning: { Icon: AlertTriangle, color: 'var(--warning-color)' },
  info: { Icon: Info, color: 'var(--info-blue)' },
}

const PLACEMENT_CLASSES = {
  topRight: 'top-4 right-4 items-end',
  topLeft: 'top-4 left-4 items-start',
  bottomRight: 'bottom-4 right-4 items-end',
  bottomLeft: 'bottom-4 left-4 items-start',
  top: 'top-4 left-1/2 -translate-x-1/2 items-center',
  bottom: 'bottom-4 left-1/2 -translate-x-1/2 items-center',
}

const DEFAULT_DURATION = 4.5

let entries = []
let sequence = 0
const listeners = new Set()
const timers = new Map()

const emit = () => listeners.forEach((listener) => listener(entries))

const removeEntry = (id) => {
  clearTimeout(timers.get(id))
  timers.delete(id)
  entries = entries.filter((entry) => entry.id !== id)
  emit()
}

const NotificationHost = () => {
  const [items, setItems] = useState([])

  useEffect(() => {
    listeners.add(setItems)
    setItems(entries)
    return () => listeners.delete(setItems)
  }, [])

  if (items.length === 0) return null

  const groups = items.reduce((acc, item) => {
    const placement = item.placement || 'topRight'
    acc[placement] = acc[placement] || []
    acc[placement].push(item)
    return acc
  }, {})

  return (
    <>
      {Object.entries(groups).map(([placement, group]) => (
        <div
          key={placement}
          className={cn(
            'ui-notification pointer-events-none fixed flex flex-col gap-3',
            PLACEMENT_CLASSES[placement] || PLACEMENT_CLASSES.topRight
          )}
          style={{ zIndex: 'var(--z-toast, 2100)' }}
          role="status"
          aria-live="polite"
        >
          {group.map((entry) => {
            const preset = ICONS[entry.type]
            return (
              <div
                key={entry.id}
                className={cn(
                  'ui-notification-notice pointer-events-auto flex w-[min(384px,90vw)] items-start gap-3 border bg-[var(--surface-card)] p-3 shadow-[var(--shadow-lg)]',
                  BORDER,
                  RADIUS_MD,
                  entry.className
                )}
                style={entry.style}
                onClick={entry.onClick}
                role={entry.onClick ? 'button' : undefined}
                tabIndex={entry.onClick ? 0 : undefined}
                onKeyDown={
                  entry.onClick
                    ? (event) => {
                        if (event.key === 'Enter' || event.key === ' ') entry.onClick()
                      }
                    : undefined
                }
              >
                {entry.icon || preset ? (
                  <span className="mt-0.5 shrink-0" style={{ color: preset?.color }}>
                    {entry.icon ?? <preset.Icon size={20} aria-hidden="true" />}
                  </span>
                ) : null}
                <div className="min-w-0 flex-1">
                  {entry.message ? (
                    <div className="ui-notification-notice-message font-semibold text-[var(--text-dark)]">
                      {entry.message}
                    </div>
                  ) : null}
                  {entry.description ? (
                    <div className="ui-notification-notice-description mt-1 text-sm text-[var(--text-body)]">
                      {entry.description}
                    </div>
                  ) : null}
                  {entry.btn ? <div className="mt-2 flex justify-end">{entry.btn}</div> : null}
                </div>
                <button
                  type="button"
                  aria-label="Fermer"
                  onClick={(event) => {
                    event.stopPropagation()
                    removeEntry(entry.id)
                    entry.onClose?.()
                  }}
                  className="ui-notification-notice-close shrink-0 rounded p-1 text-[var(--gray-600)] hover:text-[var(--text-body)]"
                >
                  <X size={14} aria-hidden="true" />
                </button>
              </div>
            )
          })}
        </div>
      ))}
    </>
  )
}

let host = null

const ensureHost = () => {
  if (host || typeof document === 'undefined') return
  host = mountDetached(() => <NotificationHost />)
}

const open = (config = {}) => {
  ensureHost()
  sequence += 1
  const id = config.key ?? `notification-${sequence}`
  const duration = config.duration ?? DEFAULT_DURATION
  const entry = { ...config, id, duration }

  entries = entries.some((item) => item.id === id)
    ? entries.map((item) => (item.id === id ? entry : item))
    : [...entries, entry]
  emit()

  if (duration > 0) {
    clearTimeout(timers.get(id))
    timers.set(
      id,
      setTimeout(() => {
        removeEntry(id)
        config.onClose?.()
      }, duration * 1000)
    )
  }

  return { key: id }
}

const notification = {
  open,
  success: (config) => open({ type: 'success', ...config }),
  error: (config) => open({ type: 'error', ...config }),
  warning: (config) => open({ type: 'warning', ...config }),
  warn: (config) => open({ type: 'warning', ...config }),
  info: (config) => open({ type: 'info', ...config }),
  close: (key) => removeEntry(key),
  destroy: () => {
    timers.forEach((timer) => clearTimeout(timer))
    timers.clear()
    entries = []
    emit()
  },
  config: () => {},
  useNotification: () => [notification, null],
}

export { NotificationHost }
export default notification
